import { ReactElement, useCallback, useState } from "react";
import { groupBy } from "lodash";
import {
  convertingAttachmentIcon,
  FileUploadConfig,
  isFileUploadValidationError,
  ProjectDecisionAttachment,
  UploadedAttachment,
  validateFiles,
} from "@/fileUpload/fileUploadDomain";
import { AlertMessageText } from "@/stores/AlertMessageStore";
import { UploadFilesPanel } from "./UploadFilesPanel";
import { createFileTypeValidationErrorMessage } from "@/fileUpload/fileUploadErrorMessages";
import { Box, VBox } from "./Box";
import { IconButton } from "./Button";
import { Icon } from "./Icon";
import { Spinner } from "./LoadingIndicator";
import { observer } from "mobx-react";
import { logError } from "@/utils/log";
import { Checkbox } from "./Checkbox";
import { stopPropagation } from "@/utils/events";
import { classNames } from "@/utils/classNames";
import { Sortable, SortableChildrenProps, SortableList } from "./SortableList";

import { useDispatch, useTranslation } from "@/stores/RootStoreContext";
import { DCEvent } from "@/events/eventsDomain";
import { DocumentType, getDocumentLabel } from "@/common/documents";
import {
  AttachmentPreview,
  autoAttachmentTypeLabels,
  UploadingAttachment,
} from "@/dbot/common/commonDomain";
import { UploadedAttachmentProps } from "@/dbot/attachments";

type AttachmentListItemProps = {
  attachment: UploadedAttachmentProps;
  checked?: boolean;
  isDeleting?: boolean;
  onDelete?: ((id: string) => void) | DCEvent[];
  onKeepOriginalFormat?: (id: string) => void;
  onOpen: ((id: string) => void) | DCEvent[];
  onToggle?: (id: string) => void;
  dragProps?: SortableChildrenProps;
};

export const AttachmentListItem = observer(
  ({
    attachment,
    onToggle,
    checked,
    isDeleting,
    onDelete,
    onOpen,
    dragProps,
    onKeepOriginalFormat,
  }: AttachmentListItemProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    return (
      <VBox className="truncate">
        <Box
          bg
          hoverBg
          rounded
          border
          alignItems={"center"}
          paddingLeft={"md"}
          paddingRight={"sm"}
          paddingY={onDelete ? "xs" : "md"}
          htmlProps={{
            onClick: () =>
              isDeleting
                ? undefined
                : Array.isArray(onOpen)
                  ? dispatch(onOpen)
                  : onOpen(attachment.id),
            tabIndex: 0,
            ...dragProps?.dragAttributes,
            ...dragProps?.dragListeners,
          }}
          className={classNames(
            "truncate",
            dragProps?.isDragging ? "cursor-grabbing" : "cursor-pointer",
            dragProps && "group",
          )}
          dropShadow={dragProps?.isDragging ? "lg" : undefined}
          tooltip={
            dragProps
              ? t(
                  attachment.source
                    ? {
                        key: "attachments.drag-tooltip.matter",
                        params: { matterTitle: attachment.source },
                      }
                    : { key: "attachments.drag-tooltip" },
                )
              : undefined
          }
        >
          <Box justifyContent={"space-between"} grow className="min-w-0">
            <Box alignItems={"center"} className="flex-1 min-w-0">
              {dragProps && (
                <Icon
                  name={"dots-six-vertical"}
                  weight={"bold"}
                  className={"subtle hidden group-hover:block"}
                />
              )}
              <Icon
                name={"paperclip"}
                className={classNames("subtle", dragProps && "group-hover:hidden")}
              />
              <span className="truncate min-w-0">{attachment.filename}</span>
            </Box>
            {attachment.source && (
              <span className="truncate min-w-0 subtle">{t(attachment.source)}</span>
            )}
          </Box>
          {attachment.icon ? (
            <Box marginLeft={"auto"}>
              <IconButton icon={attachment.icon} color="gray" tooltip={attachment.filename} />
            </Box>
          ) : (
            onDelete && (
              <Box marginLeft={"auto"}>
                {attachment.originalFileType && onKeepOriginalFormat && (
                  <IconButton
                    icon={convertingAttachmentIcon(attachment.originalFileType)}
                    tooltip={t("attachments.converting-to-pdf.tooltip", {
                      fileType: attachment.originalFileType,
                    })}
                    color={"gray"}
                    onClick={stopPropagation(() => onKeepOriginalFormat(attachment.id))}
                  />
                )}

                <IconButton
                  icon={"x"}
                  tooltip={t("delete")}
                  color="gray"
                  className={
                    attachment.originalFileType && onKeepOriginalFormat ? "-ml-2" : undefined
                  }
                  onClick={stopPropagation(() =>
                    Array.isArray(onDelete) ? dispatch(onDelete) : onDelete(attachment.id),
                  )}
                  loading={isDeleting}
                />
              </Box>
            )
          )}
          {onToggle && (
            <Box marginLeft={"auto"} padding={"sm"}>
              <Checkbox checked={checked} onChange={() => onToggle(attachment.id)} />
            </Box>
          )}
        </Box>
      </VBox>
    );
  },
);

export const UploadingAttachmentListItem = observer(
  ({
    upload,
    onKeepOriginalFormat,
  }: {
    upload: UploadingAttachment;
    onKeepOriginalFormat?: (id: string) => void;
  }) => {
    const { t } = useTranslation();
    const percent = Math.min(100, Math.max(0, Math.round(upload.progress * 100)));
    return (
      <VBox className="truncate">
        <Box
          bg
          rounded
          border
          alignItems={"center"}
          paddingLeft={"md"}
          paddingRight={"sm"}
          paddingY={"sm"}
          className={"truncate"}
        >
          <Box justifyContent={"space-between"} grow className="min-w-0" alignItems={"center"}>
            <Box alignItems={"center"} className="flex-1 min-w-0 gap-2">
              <Spinner size={"sm"} className={"color-primary"} />
              <span className="truncate min-w-0">{upload.filename}</span>
            </Box>
            <span className="truncate min-w-0 text-sm subtle">
              {upload.isConverting
                ? t("attachments.converting-to-pdf")
                : t("ai.import-status-uploading") + ` ${percent}%`}
            </span>
            {upload.isConverting && upload.originalFileType && onKeepOriginalFormat && (
              <IconButton
                icon={convertingAttachmentIcon(upload.originalFileType)}
                tooltip={t("attachments.converting-to-pdf.tooltip", {
                  fileType: upload.originalFileType,
                })}
                color={"gray"}
                onClick={() => onKeepOriginalFormat(upload.id)}
              />
            )}
            {upload.isConverting && (
              <span
                className={classNames(
                  "inline-flex p-3 text-xl subtle",
                  upload.originalFileType && onKeepOriginalFormat && "-ml-2",
                )}
              >
                <Icon name={"info"} />
              </span>
            )}
          </Box>
        </Box>
      </VBox>
    );
  },
);

export type AttachmentPanelProps = {
  fileUploadConfig?: FileUploadConfig;
  autoAttachments?: AttachmentPreview[];
  uploadingAttachments?: UploadingAttachment[];
  uploadedAttachments: UploadedAttachment[];
  includeAttachments?: UploadedAttachment[];
  onIncludeAttachment?: (id: string) => void;
  isUploading?: boolean;
  isDeleting?: (id: string) => boolean;
  onUpload?: (files: File[]) => void;
  onInvalidFile?: (errorMessage: AlertMessageText) => void;
  onOpen: (id: string) => void;
  onDelete?: (id: string) => void;
  onMove?: (attachment: UploadedAttachment, toAttachmentId: string) => void;
  onKeepOriginalFormat?: (id: string) => void;
  title?: string | ReactElement | boolean;
  className?: string;
};

export type DecisionAttachmentPanelProps = {
  fileUploadConfig?: FileUploadConfig;
  uploadingAttachments?: UploadingAttachment[];
  uploadedAttachments: ProjectDecisionAttachment[];
  includeAttachments?: ProjectDecisionAttachment[];
  currentDocumentTypes: DocumentType[];
  onIncludeAttachment?: (id: string) => void;
  isUploading?: boolean;
  isDeleting?: (id: string) => boolean;
  onUpload?: (files: File[]) => void;
  onInvalidFile?: (errorMessage: AlertMessageText) => void;
  onOpen: (id: string) => void;
  onDelete?: (id: string) => void;
  onKeepOriginalFormat?: (id: string) => void;
  onToggleDocumentType?: (
    attachment: ProjectDecisionAttachment,
    documentType: DocumentType,
  ) => void;
  title?: string | ReactElement | boolean;
  className?: string;
  isLocked: boolean;
};

export const AttachmentsPanel = observer(
  ({
    fileUploadConfig,
    autoAttachments,
    uploadingAttachments,
    uploadedAttachments,
    includeAttachments = [],
    onIncludeAttachment,
    isUploading,
    isDeleting,
    onUpload,
    onInvalidFile,
    onOpen,
    onDelete,
    onMove,
    onKeepOriginalFormat,
    title,
    className,
  }: AttachmentPanelProps) => {
    const { t } = useTranslation();
    const [draggedGroup, setDraggedGroup] = useState<string>();

    const handleUpload = useCallback(
      (files: File[]) => {
        if (files.length && !isUploading && fileUploadConfig) {
          validateFiles(files, fileUploadConfig)
            .then(onUpload)
            .catch((err) => {
              if (isFileUploadValidationError(err)) {
                onInvalidFile?.(createFileTypeValidationErrorMessage(err));
              }
            });
        }
      },
      [isUploading, onInvalidFile, onUpload, fileUploadConfig],
    );

    const conversionUploads = [...uploadedAttachments, ...includeAttachments]
      .filter((attachment) => attachment.isConverting)
      .map<UploadingAttachment>((attachment) => ({
        id: attachment.id,
        filename: attachment.filename,
        progress: 0,
        reference: attachment.id,
        isConverting: true,
        originalFileType: attachment.originalFileType,
      }));
    const uploadsById = new Map(
      [...conversionUploads, ...(uploadingAttachments ?? [])].map((upload) => [upload.id, upload]),
    );
    const allUploads = Array.from(uploadsById.values());
    const attachmentIds = new Set(
      [...uploadedAttachments, ...includeAttachments].map((attachment) => attachment.id),
    );
    const uploadsWithoutAttachment = allUploads.filter((upload) => !attachmentIds.has(upload.id));
    const listedAttachments = uploadedAttachments.filter((d) => !includeAttachments.includes(d));
    const attachmentGroups = Object.values(
      groupBy(listedAttachments, (attachment) => attachment.decisionId ?? "document"),
    );
    const renderAttachment = (
      attachment: UploadedAttachment,
      dragProps?: SortableChildrenProps,
    ) => {
      const upload = uploadsById.get(attachment.id);
      return upload ? (
        <UploadingAttachmentListItem
          key={attachment.id}
          upload={upload}
          onKeepOriginalFormat={onKeepOriginalFormat}
        />
      ) : (
        <AttachmentListItem
          attachment={attachment}
          key={attachment.id}
          isDeleting={isDeleting?.(attachment.id)}
          onOpen={onOpen}
          onDelete={onDelete}
          onKeepOriginalFormat={onKeepOriginalFormat}
          dragProps={dragProps}
        />
      );
    };

    return (
      <VBox className={className}>
        {title !== false && (
          <div>
            {title === true || title === undefined
              ? `${t("attachments")} (${uploadedAttachments.length})`
              : title}
          </div>
        )}
        {autoAttachments &&
          autoAttachments.map((a) => (
            <AttachmentListItem
              attachment={{
                id: a.type,
                filename: t(autoAttachmentTypeLabels[a.type]),
                source: { key: "common.auto" },
                icon: "sparkle",
              }}
              key={a.type}
              onOpen={onOpen}
            />
          ))}
        {includeAttachments &&
          onIncludeAttachment &&
          includeAttachments.map((attachment) => {
            const upload = uploadsById.get(attachment.id);
            return upload ? (
              <UploadingAttachmentListItem
                key={attachment.id}
                upload={upload}
                onKeepOriginalFormat={onKeepOriginalFormat}
              />
            ) : (
              <AttachmentListItem
                attachment={attachment}
                key={attachment.id}
                onOpen={onOpen}
                onToggle={onIncludeAttachment}
                checked={uploadedAttachments.includes(attachment)}
              />
            );
          })}
        {!onMove && listedAttachments.map((attachment) => renderAttachment(attachment))}
        {onMove &&
          attachmentGroups.map((attachments) => {
            const group = attachments[0].decisionId ?? "document";
            const isOtherGroupDragged = draggedGroup !== undefined && draggedGroup !== group;
            return (
              <SortableList
                key={group}
                items={attachments}
                onMove={(attachment, toAttachment) => onMove(attachment, toAttachment.id)}
                onDraggingChange={(isDragging) => setDraggedGroup(isDragging ? group : undefined)}
              >
                {attachments.map((attachment) => (
                  <Sortable
                    key={attachment.id}
                    id={attachment.id}
                    isDragDisabled={
                      attachments.length < 2 ||
                      uploadsById.has(attachment.id) ||
                      isOtherGroupDragged
                    }
                    className={isOtherGroupDragged ? "opacity-50 pointer-events-none" : undefined}
                  >
                    {(dragProps) =>
                      renderAttachment(attachment, dragProps.isDragDisabled ? undefined : dragProps)
                    }
                  </Sortable>
                ))}
              </SortableList>
            );
          })}
        {uploadsWithoutAttachment.map((upload) => (
          <UploadingAttachmentListItem
            key={upload.id}
            upload={upload}
            onKeepOriginalFormat={onKeepOriginalFormat}
          />
        ))}
        {onUpload && fileUploadConfig && (
          <UploadFilesPanel
            fileUploadConfig={fileUploadConfig}
            isUploading={isUploading}
            onUpload={handleUpload}
          />
        )}
      </VBox>
    );
  },
);

export const DecisionAttachmentsPanel = observer(
  ({
    fileUploadConfig,
    uploadingAttachments,
    uploadedAttachments,
    includeAttachments = [],
    currentDocumentTypes,
    onIncludeAttachment,
    isUploading,
    isDeleting,
    onUpload,
    onInvalidFile,
    onOpen,
    onDelete,
    onKeepOriginalFormat,
    onToggleDocumentType,
    title,
    className,
    isLocked,
  }: DecisionAttachmentPanelProps) => {
    const { t } = useTranslation();

    const handleUpload = useCallback(
      (files: File[]) => {
        if (files.length && !isUploading && fileUploadConfig) {
          validateFiles(files, fileUploadConfig)
            .then(onUpload)
            .catch((err) => {
              logError("Invalid attachments err", files);
              if (isFileUploadValidationError(err)) {
                onInvalidFile?.(createFileTypeValidationErrorMessage(err));
              }
            });
        }
      },
      [isUploading, onInvalidFile, onUpload, fileUploadConfig],
    );

    const conversionUploads = [...uploadedAttachments, ...includeAttachments]
      .filter((attachment) => attachment.isConverting)
      .map<UploadingAttachment>((attachment) => ({
        id: attachment.id,
        filename: attachment.filename,
        progress: 0,
        reference: attachment.id,
        isConverting: true,
        originalFileType: attachment.originalFileType,
      }));
    const uploadsById = new Map(
      [...conversionUploads, ...(uploadingAttachments ?? [])].map((upload) => [upload.id, upload]),
    );
    const allUploads = Array.from(uploadsById.values());
    const attachmentIds = new Set(
      [...uploadedAttachments, ...includeAttachments].map((attachment) => attachment.id),
    );
    const uploadsWithoutAttachment = allUploads.filter((upload) => !attachmentIds.has(upload.id));

    return (
      <VBox className={className}>
        {title !== false && (
          <div>
            {title === true || title === undefined
              ? `${t("attachments")} (${uploadedAttachments.length})`
              : title}
          </div>
        )}
        {includeAttachments &&
          onIncludeAttachment &&
          includeAttachments.map((attachment) => {
            const upload = uploadsById.get(attachment.id);
            if (upload) {
              return (
                <UploadingAttachmentListItem
                  key={attachment.id}
                  upload={upload}
                  onKeepOriginalFormat={onKeepOriginalFormat}
                />
              );
            }
            return (
              <VBox key={attachment.id} justifyContent={"start"}>
                <AttachmentListItem
                  attachment={attachment}
                  onOpen={onOpen}
                  onToggle={onIncludeAttachment}
                  checked={uploadedAttachments.includes(attachment)}
                />
                <div>{t("decisions.freetext.attachment.choose-documents")}</div>
                {currentDocumentTypes.map((docType) => (
                  <Box key={docType} paddingX>
                    <Checkbox
                      label={t(getDocumentLabel(docType))}
                      onChange={() => onToggleDocumentType?.(attachment, docType)}
                      checked={attachment.selectedDocuments?.includes(docType)}
                      disabled={isLocked}
                    />
                  </Box>
                ))}
              </VBox>
            );
          })}
        {uploadedAttachments
          .filter((d) => !includeAttachments.includes(d))
          .map((attachment) => {
            const upload = uploadsById.get(attachment.id);
            if (upload) {
              return (
                <UploadingAttachmentListItem
                  key={attachment.id}
                  upload={upload}
                  onKeepOriginalFormat={onKeepOriginalFormat}
                />
              );
            }
            return (
              <VBox key={attachment.id} justifyContent={"start"}>
                <AttachmentListItem
                  attachment={attachment}
                  isDeleting={isDeleting?.(attachment.id)}
                  onOpen={onOpen}
                  onDelete={onDelete}
                  onKeepOriginalFormat={onKeepOriginalFormat}
                />
                <div>{t("decisions.freetext.attachment.choose-documents")}</div>
                {currentDocumentTypes.map((docType) => (
                  <Box key={docType} paddingX>
                    <Checkbox
                      label={t(getDocumentLabel(docType))}
                      onChange={() => onToggleDocumentType?.(attachment, docType)}
                      checked={attachment.selectedDocuments?.includes(docType)}
                      disabled={isLocked}
                    />
                  </Box>
                ))}
              </VBox>
            );
          })}
        {uploadsWithoutAttachment.map((upload) => (
          <UploadingAttachmentListItem
            key={upload.id}
            upload={upload}
            onKeepOriginalFormat={onKeepOriginalFormat}
          />
        ))}
        {onUpload && fileUploadConfig && (
          <UploadFilesPanel
            fileUploadConfig={fileUploadConfig}
            isUploading={isUploading}
            onUpload={handleUpload}
          />
        )}
      </VBox>
    );
  },
);
